<template>
	<div
		v-if="shouldDisplay && videoSrc"
		:id="blok.block_id || null"
		:data-transparent-header="blok.transparent_header ? '' : null"
		class="relative"
		:class="`${blok.full_width ? 'full-bleed' : 'main-wrapper'} ${blok.offset || ''} ${blok.visibility ? blok.visibility.join(' ') : ''} ${isFixedHeight ? 'overflow-hidden flex justify-center' : ''}`"
	>
		<div
			class="hero-video relative overflow-hidden"
			:class="isFixedHeight ? 'hero-max-w-none' : 'w-full'"
			:style="blockHeight"
		>
			<div
				v-if="hasPoster"
				aria-hidden="true"
				class="w-full"
				:class="isFixedHeight ? 'h-full' : ''"
			>
				<NuxtImg
					v-if="desktopPosterSrc"
					:src="desktopPosterSrc"
					alt="poster"
					class="hidden lg:block w-full"
					:class="isFixedHeight ? 'h-full object-cover' : ''"
					provider="storyblok"
					:preload="{ fetchPriority: 'high' }"
					loading="eager"
					fetchpriority="high"
					format="webp"
					quality="80"
				/>
				<NuxtImg
					v-if="mobilePosterSrc"
					:src="mobilePosterSrc"
					alt="poster"
					class="block lg:hidden w-full"
					:class="isFixedHeight ? 'h-full object-cover' : ''"
					provider="storyblok"
					:preload="{ fetchPriority: 'high' }"
					loading="eager"
					fetchpriority="high"
					format="webp"
					quality="80"
				/>
			</div>
			<HeroVideoEmbed
				v-if="blok.is_iframe"
				:src="videoSrc"
				:src-mobile="mobileVideoSrc"
				:title="blok.heading"
				:autoplay="blok.autoplay"
				:cover="hasPoster || isFixedHeight"
				:wrapper-class="embedWrapperClass"
			/>
			<video
				v-else
				class="w-full"
				:class="[
					hasPoster ? 'absolute inset-0 h-full' : '',
					isFixedHeight ? 'object-cover' : ''
				]"
				loop
				:controls="blok.controls"
				:muted="blok.muted"
				:autoplay="blok.autoplay"
				:preload="blok.autoplay ? 'metadata' : 'none'"
			>
				<source
					v-if="videoSrc"
					:src="videoSrc"
					type="video/mp4"
					media="(min-width: 1024px)"
				/>
				<source
					:src="mobileVideoSrc || videoSrc"
					type="video/mp4"
					media="(max-width: 1023px)"
				/>
			</video>
		</div>
		<div
			class="absolute w-full lg:w-full top-0 bottom-0 left-1/2 -translate-x-1/2 z-10 flex flex-col text-white main-wrapper lg:p-7.5"
			:class="`${blok.content_position || 'justify-center items-center'} ${blok.transparent_header ? 'pt-8 lg:pt-9' : ''}`"
		>
			<div
				class="grid grid-cols-1 gap-4 lg:py-12 py-6 px-6"
				:class="blok.text_align || ''"
			>
				<h2 v-if="blok.heading" :class="blok.heading_styles || ''">
					{{ blok.heading }}
				</h2>
				<h3 v-if="blok.sub_heading" :class="blok.sub_heading_styles || ''">
					{{ blok.sub_heading }}
				</h3>
				<p v-if="blok.text" :class="blok.text_styles || ''">
					{{ blok.text }}
				</p>
				<div
					v-if="blok.call_to_action?.length > 0"
					class="flex flex-col"
					:class="blok.content_position || 'justify-center items-center'"
				>
					<StoryblokComponent
						v-for="call_to_action in blok.call_to_action"
						:key="call_to_action._uid"
						:blok="call_to_action"
					/>
				</div>
				<div
					v-if="blok.countdown_start_date"
					class="flex flex-col"
					:class="blok.content_position || 'justify-center items-center'"
				>
					<CountdownBlock
						:start-date="blok.countdown_start_date"
						wrapper-class=" flex justify-center w-full"
						time-item-class="lg:!text-4xl !text-2xl"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { BlokVideo } from "~/types/storyblok/storyblok";

const HEIGHT_UNITS = {
	pixel: "px",
	percent: "%",
	viewport: "vh"
};

interface Props {
	blok: BlokVideo;
}
const { blok } = defineProps<Props>();

const { shouldDisplay } = useDisplayBlock(blok.start_date, blok.end_date);

const videoSrc = computed(() => blok?.src || blok?.file?.filename);
const mobileVideoSrc = computed(
	() => blok?.src_mobile || blok?.file_mobile?.filename
);
const desktopPosterSrc = computed(
	() => blok?.poster_desktop?.filename || blok?.poster_mobile?.filename
);
const mobilePosterSrc = computed(
	() => blok?.poster_mobile?.filename || blok?.poster_desktop?.filename
);
const hasPoster = computed(
	() => !!(desktopPosterSrc.value || mobilePosterSrc.value)
);
const isFixedHeight = computed(() => !!blok.height_desktop);

// A poster already establishes the block's box, so the embed only has to fill
// it. Undefined leaves the embed on its own box, which reads the height custom
// properties inherited from `.hero-video` and falls back to 16:9 without them.
const embedWrapperClass = computed(() =>
	hasPoster.value ? "absolute inset-0 w-full h-full" : undefined
);

const blockHeight = computed(() => {
	const units = HEIGHT_UNITS[blok.height_units] ?? "px";
	const desktopHeight = blok.height_desktop;
	const mobileHeight = blok.height_mobile || desktopHeight;

	if (!desktopHeight) return {};

	return {
		"--mobile-height": `${mobileHeight}${units}`,
		"--desktop-height": `${desktopHeight}${units}`
	};
});
</script>
